import { AccountWidget } from '@organisms/AccountWidget';
import type { FundingConfigurations } from 'api';
import { AccountStatus, useGetCollectionFundingInstructions } from 'api';

import type { VirtualAccount } from 'models/virtualAccount';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';

import { AccountWidgetSkeleton } from '@molecules/AccountWidgetSkeleton';

import { DASHBOARD_ACCOUNT_WIDGET } from './constants';

type Props = {
  classNames?: string;
};

const getIsFundingUpdated = (
  fundingInstructions: FundingConfigurations | undefined,
  accountNumber: string
) => {
  const accountFundingsList = fundingInstructions?.[accountNumber];

  return accountFundingsList?.some((item) => item.changed === true);
};

export const DashboardAccountWidgets = ({ classNames }: Props) => {
  const organisationId = useOrganisationId<number>();
  const {
    accounts: accountsInfo,
    isFetching,
    isSuccess
  } = useVirtualAccounts();
  const {
    data: { data: fundingInstructions } = {},
    isSuccess: isFundingSuccess
  } = useGetCollectionFundingInstructions(organisationId);
  const sortedAccountsInfo = [...(accountsInfo ?? [])].sort((a, b) => {
    const aIsTerminated = a.status === AccountStatus.terminated;
    const bIsTerminated = b.status === AccountStatus.terminated;

    if (aIsTerminated === bIsTerminated) {
      return 0;
    }

    return aIsTerminated ? 1 : -1;
  });

  const hasAccounts = (accountsInfo?.length ?? 0) > 0;

  return (
    <div className={classNames}>
      {isSuccess &&
        (hasAccounts ? (
          <section
            className="flex flex-col flex-wrap gap-2"
            aria-label="Account balances"
          >
            {sortedAccountsInfo.map((accountInfo: VirtualAccount) => {
              const isFundingUpdated = getIsFundingUpdated(
                isFundingSuccess ? fundingInstructions : undefined,
                accountInfo.account_number
              );

              return (
                <AccountWidget
                  key={accountInfo.account_number}
                  accountInfo={accountInfo}
                  isFundingUpdated={isFundingUpdated}
                  data-testid={DASHBOARD_ACCOUNT_WIDGET}
                />
              );
            })}
          </section>
        ) : (
          <div className="w-full rounded-2xl bg-white p-8 text-center shadow-plain-lg">
            <h2 className="text-xl font-medium">No data found</h2>
            <p className="mt-6">There are not yet accounts available.</p>
          </div>
        ))}
      {isFetching && (
        <div role="progressbar" className="mb-4 flex flex-wrap gap-4">
          <AccountWidgetSkeleton />
          <AccountWidgetSkeleton />
          <AccountWidgetSkeleton />
        </div>
      )}
      {!isFetching && !isSuccess && (
        <div className="flex h-40 w-full items-center justify-center rounded-2xl bg-white p-4 text-xs shadow-plain-lg">
          Accounts information is temporarily unavailable.
        </div>
      )}
    </div>
  );
};
